<script setup lang="ts">
  import {computed, ref} from 'vue';
  import useCpBem from '~/composables/useCpBem';
  import type {inputRules} from '~/types';

  const props = withDefaults(defineProps<{
      leadingIcon?: string,
      trailingIcon?: string,
      helperText?: string,
      characterCounter?: boolean,
      disabled?: boolean,
      label?: string,
      modelValue: string,
      maxlength?: number,
      required?: boolean,
      type?: string,
      rules?: inputRules[],
      prefix?: string,
      persistentCounter?: boolean,
      customMessage?: boolean,
      hideDetails?: boolean | 'auto',
      error?: boolean,
      errorMessage?: string,
      customInnerIcon?: boolean,
      onInput?: any,
      customPrependInnerIcon?: boolean,
      id?: string,
      name?: string,
  }>(), {
    hideDetails: 'auto',
  });

  defineEmits({
    'update:model-value': null,
    'click:append-inner': null,
    'update:focused': null,
  });

  const requiredValue = computed(() => {
    return props.required ? '*' : '';
  });

  const value = ref();

  const validateMaxLength = (value: string) => {
    if (props.maxlength !== undefined && value.length > props.maxlength && props.type !== 'number') {
      return value.slice(0, props.maxlength);
    } else {
      return value;
    }
  };
  const {b, e} = useCpBem('cp-text-field');
</script>

<template>
    <div :class="b">
        <v-theme-provider theme="cpMobile">
            <v-text-field
                :id="id"
                :name="name"
                :model-value="modelValue"
                :class="e('input')"
                :label="`${requiredValue}${label?? ''}`"
                :append-inner-icon="trailingIcon"
                :prepend-inner-icon="leadingIcon"
                :disabled="disabled!"
                :maxlength="maxlength"
                :hint="helperText"
                :hide-details="hideDetails"
                density="comfortable"
                variant="solo"
                :counter="characterCounter !== undefined && characterCounter ? true: undefined"
                bg-color="none"
                color="primary"
                :flat="true"
                :type="type!"
                :rules="rules!"
                :prefix="prefix?? ''"
                :persistent-counter="persistentCounter ?? true"
                :persistent-hint="true"
                :error="error"
                :error-messages="errorMessage"
                :oninput="onInput"
                @update:model-value="$emit('update:model-value', $event); value = $event;"
                @click:append-inner="$emit('click:append-inner', $event)"
                @keyup="$emit('update:model-value', validateMaxLength(value))"
                @update:focused="$emit('update:focused', $event)"
            >
                <template v-if="customMessage" #message>
                    <slot name="message" />
                </template>
                <template v-if="customInnerIcon" #append-inner>
                    <slot name="innerIcon" />
                </template>
                <template v-if="customPrependInnerIcon" #prepend-inner>
                    <slot />
                </template>
            </v-text-field>
        </v-theme-provider>
    </div>
</template>

<style lang="scss">
  .cp-text-field {
    &__input {
      width: 100%;
      outline: none;
    }

    .v-text-field__prefix {
      padding-bottom: 8px;
    }

    .v-input--density-comfortable .v-field--variant-solo .v-label.v-field-label--floating,
    .v-input--density-comfortable .v-field--variant-solo-inverted .v-label.v-field-label--floating,
    .v-input--density-comfortable .v-field--variant-filled .v-label.v-field-label--floating,
    .v-input--density-comfortable .v-field--variant-solo-filled .v-label.v-field-label--floating {
      top: 4px;
    }

    .v-text-field--prefixed.v-text-field .v-field__input {
      --v-field-padding-start: 4px;
      padding: calc(8px + 12px) 16px 8px 4px;
    }

    .v-messages {
      font-family: var(--cp-font-family);
      font-size: var(--cp-caption-size);
      line-height: var(--cp-caption-line-height);
      letter-spacing: 0;
      color: var(--color-neutral);
      opacity: unset;

      &--error {
        color: var(--color-critical-dark);
      }
    }

    .v-counter {
      margin-left: $cp-gutter;
      font-family: var(--cp-font-family);
      font-size: var(--cp-caption-size);
      line-height: var(--cp-caption-line-height);
      letter-spacing: 0;
      color: var(--color-neutral);
      opacity: unset;
      min-width: 30px;
    }

    .v-label {
      color: var(--color-primary);
      opacity: 1;

      & .v-field--disabled {
        color: var(--color-neutral);
      }
    }

    .v-field--disabled .v-field__field > .v-label {
      color: var(--color-neutral);
    }

    .v-field {
      &--variant-solo {
        background: none;
        box-shadow: none;
        border-radius: $cp-border-radius;
        border: 1px solid var(--color-neutral);
      }

      &--active {
        border: $cp-border-primary;
      }

      &--error {
        border: $cp-border-critical;

        &:not(.v-field--disabled) .v-label.v-field-label--floating {
          color: var(--color-critical-dark);
        }

        &:not(.v-field--disabled) .v-label.v-field-label {
          color: var(--color-critical-dark);
        }
      }

      &__append-inner {
        color: var(--color-primary);

        & > .v-icon {
          opacity: 1;

          & > .v-field--disabled {
            color: var(--color-neutral);
          }
        }
      }

      &__field {
        & .v-label.v-field-label {
          font-family: var(--cp-font-family);
          font-size: var(--cp-body-bold-size);
          line-height: var(--cp-body-bold-line-height);
          margin-top: $cp-gutter-xsmall;
          font-weight: 600;
          display: inline;

          &--floating {
            max-width: calc(100% - $cp-gutter-large);
            line-height: var(--cp-caption-bold-line-height);
            font-size: var(--cp-caption-bold-size);
            --v-field-label-scale: 0.833em;
          }
        }

        & input {
          font-family: var(--cp-font-family);
          font-size: var(--cp-body-regular-size);
          line-height: var(--cp-body-regular-line-height);
          color: var(--color-neutral-dark);

          &:disabled {
            color: var(--color-neutral);
          }
        }

        & input[type=number] {
          -moz-appearance: textfield;
        }

        & input[type=text] {
          font-weight: var(--cp-regular);
        }

        & input[type=password] {
          font-weight: var(--cp-bold);
        }

        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }

      &__append-inner {
        padding-top: 0;
        align-items: center;
      }

      &--appended {
        padding-inline-end: $cp-gutter;
      }

      &--prepended {
        padding-inline-start: $cp-gutter;
      }
    }

    .v-field--disabled {
      opacity: unset;
      border: $cp-border-disabled;

      & .v-field__prepend-inner > .v-icon {
        color: var(--color-neutral-light);
      }

      & .v-field__append-inner > .v-icon {
        color: var(--color-neutral-light);
      }
    }

    .v-input {
      &--disabled .v-input__details {
        opacity: unset;
      }

      &__details {
        align-items: flex-start;
        height: fit-content;
        padding-top: $cp-gutter-xsmall;
      }

      &--error:not(&--disabled) {
        & .v-input__details {
          & .v-messages {
            color: var(--color-critical-dark);
          }
        }

        & .v-counter {
          color: var(--color-critical-dark);
        }
      }
    }
  }
</style>
